import React from 'react';
import { useQueryClient } from 'react-query';
import useEffectOnce from 'react-use/lib/useEffectOnce';
import { useDocumentTitle } from '../../../../../../_hooks/useDocumentTitle';
import { EmptyState } from '../../../../../../CommonComponents/OPL/AsyncStates/EmptyState/EmptyState';
import { Box } from '../../../../../../CommonComponents/OPL/Box/Box';
import { FullPageLoading } from '../../../../../../CommonComponents/OPL/FullpageLoading/FullPageLoading';
import { Text } from '../../../../../../CommonComponents/OPL/Text/Text';
import { PageWrapper } from '../../../../../../CommonComponents/PageWrapper/PageWrapper';
import { usePlans } from '../../../../hooks/usePlans';
import { queryKeys } from './api/queryKeys';
import { useComparePlansQuery } from './api/useComparePlansQuery';
import { CompareTable } from './components/CompareTable/CompareTable';
import { PlansSelector } from './components/PlansSelector/PlansSelector';
import { navigateToRetirementSideBySide } from './utils/navigateToRetirementSideBySide';

interface Props {
  clientId: string;
  planIds: string[];
  goalId: string;
}

export const RetirementSideBySide = ({ clientId, planIds, goalId }: Props) => {
  const { data } = useComparePlansQuery({ clientId, planIds });

  const plans = usePlans({ clientId });

  const queryClient = useQueryClient();

  // We want to cache the results only when the user is inside this page
  useEffectOnce(() => () => queryClient.removeQueries(queryKeys.comparePlansAll(clientId)));

  useDocumentTitle('Compare Plans');

  if (plans.length === 0) {
    return <FullPageLoading />;
  }

  if (data && data.length === 0) {
    return <EmptyState mt="56" />;
  }

  return (
    <PageWrapper>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Text as="h1" fontSize="h3" fontWeight={600}>
          Compare Plans
        </Text>

        <PlansSelector
          clientId={clientId}
          defaultSelected={planIds}
          onApply={_planIds => {
            navigateToRetirementSideBySide(clientId, _planIds, goalId);
          }}
        />
      </Box>

      <CompareTable clientId={clientId} planIds={planIds} goalId={goalId} />
    </PageWrapper>
  );
};
